import * as RadixDialog from '@radix-ui/react-dialog';
import { CheckCircle2, X } from 'lucide-react';
import { useCallback, useEffect, useRef, useState, type ReactElement, type ReactNode } from 'react';

import { ActionMenu, type ActionItem } from './patterns.js';

import './page.css';

/**
 * Gabarit de page progressif (docs/ux/proposition-pages.md) : une action principale à droite
 * du titre, les secondaires dans « Actions ▾ » (`ActionBar`) ; toute saisie à la demande dans
 * un tiroir latéral (`Drawer`) ; confirmation en fenêtre (`ConfirmDialog`) ; message de
 * réussite unique sous l'en-tête (`useFlash` + `Flash`).
 */

/**
 * Actions de l'en-tête, toujours dans le même ordre : « Actions ▾ » puis le bouton principal
 * (orange, à droite). Rien si aucune action.
 */
export function ActionBar({
  primary,
  more = [],
  moreLabel = 'Actions',
}: {
  /** Bouton principal (`variant="primary"`), un seul. */
  primary?: ReactNode;
  /** Actions secondaires ; sensibles en fin, après `'separator'`, `danger`. */
  more?: readonly (ActionItem | false | null | undefined)[];
  moreLabel?: string;
}): ReactElement | null {
  const items = more.filter((i): i is ActionItem => i !== false && i != null);
  const hasMenu = items.some((i) => i !== 'separator' && i.hidden !== true);
  if (!hasMenu && (primary === undefined || primary === null || primary === false)) return null;
  return (
    <div className="o-actionbar">
      {hasMenu && <ActionMenu items={items} label={moreLabel} />}
      {primary}
    </div>
  );
}

/**
 * Tiroir latéral (à droite, pleine hauteur ; plein écran au téléphone) : formulaire ouvert à
 * la demande depuis une fiche ou une liste. Focus piégé, Échap ferme, titre annoncé. Les
 * boutons vont dans `footer` (en bas à droite : [Annuler] [■ Enregistrer …]) ; pour un
 * formulaire, donner `form="id"` au bouton d'envoi. `centered` : grande fenêtre centrée sur la
 * plus grande partie de l'écran, pour un formulaire long à éditer (documents d'une activité).
 */
export function Drawer({
  open,
  onOpenChange,
  title,
  description,
  children,
  footer,
  wide = false,
  centered = false,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  description?: string | undefined;
  children: ReactNode;
  footer?: ReactNode;
  /** 760 px au lieu de 520 px (tableau, formulaire en étapes). */
  wide?: boolean;
  /** Fenêtre centrée occupant la plus grande partie de l'écran (plein écran au téléphone). */
  centered?: boolean;
}): ReactElement {
  const className = ['o-drawer', wide ? 'o-drawer--wide' : '', centered ? 'o-drawer--centered' : '']
    .filter(Boolean)
    .join(' ');
  return (
    <RadixDialog.Root open={open} onOpenChange={onOpenChange}>
      <RadixDialog.Portal>
        <RadixDialog.Overlay className="o-overlay o-drawer-overlay" />
        <RadixDialog.Content className={className}>
          <div className="o-drawer__header">
            <div>
              <RadixDialog.Title asChild>
                <h2>{title}</h2>
              </RadixDialog.Title>
              {description === undefined ? (
                <RadixDialog.Description className="o-visually-hidden">
                  {title}
                </RadixDialog.Description>
              ) : (
                <RadixDialog.Description className="o-drawer__description">
                  {description}
                </RadixDialog.Description>
              )}
            </div>
            <RadixDialog.Close asChild>
              <button
                type="button"
                className="o-button o-button--ghost o-icon-button"
                aria-label="Fermer"
              >
                <X aria-hidden="true" />
              </button>
            </RadixDialog.Close>
          </div>
          <div className="o-drawer__body o-stack">{children}</div>
          {footer !== undefined && <div className="o-drawer__footer">{footer}</div>}
        </RadixDialog.Content>
      </RadixDialog.Portal>
    </RadixDialog.Root>
  );
}

/**
 * Confirmation d'une action sensible (supprimer, annuler, sortir du parc, prévenir du monde) :
 * la conséquence en une phrase, deux boutons nommés. Remplace `window.confirm`.
 */
export function ConfirmDialog({
  open,
  onOpenChange,
  title,
  children,
  confirmLabel,
  cancelLabel = 'Annuler',
  danger = false,
  pending = false,
  onConfirm,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  /** Conséquence, en une ou deux phrases ; peut contenir un champ (motif). */
  children: ReactNode;
  confirmLabel: string;
  cancelLabel?: string;
  danger?: boolean;
  pending?: boolean;
  onConfirm: () => void;
}): ReactElement {
  return (
    <RadixDialog.Root open={open} onOpenChange={onOpenChange}>
      <RadixDialog.Portal>
        <RadixDialog.Overlay className="o-overlay" />
        <RadixDialog.Content className="o-dialog o-confirm" role="alertdialog">
          <RadixDialog.Title asChild>
            <h2 className="o-confirm__title">{title}</h2>
          </RadixDialog.Title>
          <RadixDialog.Description asChild>
            <div className="o-confirm__body">{children}</div>
          </RadixDialog.Description>
          <div className="o-drawer__footer o-confirm__footer">
            <RadixDialog.Close asChild>
              <button type="button" className="o-button">
                {cancelLabel}
              </button>
            </RadixDialog.Close>
            <button
              type="button"
              className={danger ? 'o-button o-button--danger' : 'o-button o-button--primary'}
              disabled={pending}
              onClick={onConfirm}
            >
              {confirmLabel}
            </button>
          </div>
        </RadixDialog.Content>
      </RadixDialog.Portal>
    </RadixDialog.Root>
  );
}

/** Message de réussite d'une page : `show("Plein enregistré.")`, effacé seul après `delay`. */
export function useFlash(delay = 6000): {
  message: string | null;
  show: (message: string) => void;
  clear: () => void;
} {
  const [message, setMessage] = useState<string | null>(null);
  const timer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const clear = useCallback(() => {
    clearTimeout(timer.current);
    setMessage(null);
  }, []);
  const show = useCallback(
    (next: string) => {
      clearTimeout(timer.current);
      setMessage(next);
      timer.current = setTimeout(() => {
        setMessage(null);
      }, delay);
    },
    [delay],
  );
  useEffect(
    () => () => {
      clearTimeout(timer.current);
    },
    [],
  );
  return { message, show, clear };
}

/** Bandeau de réussite, sous l'en-tête de page, annoncé aux lecteurs d'écran. */
export function Flash({
  message,
  onClose,
}: {
  message: string | null;
  onClose?: (() => void) | undefined;
}): ReactElement {
  // Zone toujours présente (vide) pour que l'annonce soit lue à l'apparition du message.
  return (
    <div className="o-flash-zone" role="status" aria-live="polite">
      {message !== null && (
        <div className="o-flash">
          <CheckCircle2 aria-hidden="true" size={18} />
          <span className="o-flash__text">{message}</span>
          {onClose !== undefined && (
            <button
              type="button"
              className="o-button o-button--ghost o-icon-button o-flash__close"
              aria-label="Fermer le message"
              onClick={onClose}
            >
              <X aria-hidden="true" size={16} />
            </button>
          )}
        </div>
      )}
    </div>
  );
}

/**
 * Ligne de titre d'une liste dans une fiche ou un onglet : « 4 contrôles enregistrés » à
 * gauche, « Ajouter … » à droite. Pas de cadre (la liste suit).
 */
export function ListHeader({
  children,
  action,
}: {
  children: ReactNode;
  action?: ReactNode;
}): ReactElement {
  return (
    <div className="o-list-header">
      <p className="o-list-header__text">{children}</p>
      {action !== undefined && action !== null && action !== false && (
        <div className="o-list-header__action">{action}</div>
      )}
    </div>
  );
}
